import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { CustomerProfile } from '../components/CustomerProfile'
import { Customer, makeApiCall } from '../utils/customerUtils'
import { PageContent } from '../components/Layout'

const CustomerProfilePage: React.FC = () => {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [customer, setCustomer] = useState<Customer | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (id) {
      fetchCustomer()
    }
  }, [id])

  const fetchCustomer = async () => {
    try {
      setLoading(true)
      console.log('Fetching customer with ID:', id)
      const response = await makeApiCall(`/api/customers/${id}`)
      console.log('API response status:', response.status)
      
      if (response.ok) {
        const data = await response.json()
        console.log('API response data:', data)
        // Backend returns { message: '...', customer: { ... } }
        setCustomer(data.customer)
      } else {
        const errorData = await response.json()
        console.error('API error response:', errorData)
        setError('Customer not found')
      }
    } catch (error) {
      console.error('Error fetching customer:', error)
      setError('Failed to load customer')
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = (customer: Customer) => {
    // Navigate to edit mode; pass returnTo so saving redirects back here
    navigate(`/dashboard/customers?edit=${customer.id}&returnTo=/dashboard/customers/${customer.id}`)
  }

  const handleClose = () => {
    navigate('/dashboard/customers')
  }

  const handleCustomerClick = (customerId: number) => {
    navigate(`/dashboard/customers/${customerId}`)
  }

  const handleDealClick = (dealId: number) => {
    navigate(`/dashboard/deals/${dealId}`)
  }

  if (loading) {
    return (
      <PageContent>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      </PageContent>
    )
  }

  if (error || !customer) {
    return (
      <PageContent>
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            {t('customers.customerNotFound')}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {t('customers.customerNotFoundDescription')}
          </p>
          <button
            onClick={() => navigate('/dashboard/customers')}
            className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
          >
            {t('customers.backToCustomers')}
          </button>
        </div>
      </PageContent>
    )
  }

  return (
    <PageContent>
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
              {customer.first_name} {customer.last_name}
            </h1>
            {customer.referred_by && (
              <div className="mt-2 flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('customers.form.referredBy')}:
                </span>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {customer.referred_by}
                </span>
              </div>
            )}
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => handleEdit(customer)}
              className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-gray-800 to-gray-900 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
            >
              <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              {t('common.edit')}
            </button>
            <button
              onClick={handleClose}
              className="inline-flex items-center px-4 py-2 bg-gray-600 text-white font-medium rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              {t('customers.backToList')}
            </button>
          </div>
        </div>
      </div>
      <CustomerProfile
        customerId={customer.id}
        onEdit={handleEdit}
        onClose={handleClose}
        onCustomerClick={handleCustomerClick}
        onDealClick={handleDealClick}
      />
    </PageContent>
  )
}

export default CustomerProfilePage
